import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Lock, Menu, Plus, ShieldCheck, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/DropdownMenu';
import { ExportMenu } from '../export/ExportMenu';
import { ThemeToggle } from '../ThemeToggle';
import { useAuth } from '../../contexts/AuthContext';
import { useStudents } from '../../contexts/StudentsContext';
import { navItems } from '../../data/navigation';
import { initialsOf } from '../../utils/format';
interface TopbarProps {
  onOpenMobileNav: () => void;
  sidebarCollapsed: boolean;
}
export function Topbar({
  onOpenMobileNav,
  sidebarCollapsed
}: TopbarProps) {
  const {
    user,
    logout,
    lock
  } = useAuth();
  const {
    openCreateForm
  } = useStudents();
  const location = useLocation();
  const navigate = useNavigate();
  const active = navItems.find(item => location.pathname.startsWith(item.to)) ?? navItems[0];
  const displayName = user?.displayName || user?.email || 'Signed in';
  const handleSignOut = async () => {
    await logout();
    toast.success('Signed out safely.');
    navigate('/', {
      replace: true
    });
  };
  const handleLock = () => {
    lock();
    toast.info('App locked. Enter your master password to continue.');
    navigate('/security', {
      replace: true
    });
  };
  return <header className={`glass sticky top-0 z-20 flex h-16 items-center gap-3 rounded-b-3xl border-t-0 px-4 transition-[margin] duration-300 sm:px-6 ${sidebarCollapsed ? 'lg:ml-[84px]' : 'lg:ml-[264px]'}`} data-id="element-198" data-mp="1222z1t05di6l">
      <button type="button" onClick={onOpenMobileNav} aria-label="Open navigation" className="grid h-9 w-9 place-items-center rounded-xl border border-[var(--glass-hairline)] text-muted-foreground transition-colors hover:text-foreground lg:hidden" data-id="element-199" data-mp="3hq2xeauuzy3t">
        <Menu className="h-4 w-4" data-id="element-200" data-mp="1ln6iglzyml4g" />
      </button>

      <div className="min-w-0 flex-1" data-id="element-201" data-mp="1lnyf805amowb">
        <h1 className="truncate text-base font-semibold tracking-tight text-foreground" data-id="element-202" data-mp="1qn2nmoke8ill">{active.label}</h1>
        <p className="hidden truncate text-xs text-muted-foreground sm:block" data-id="element-203" data-mp="1vmfbncn03vpr">{active.description}</p>
      </div>

      <div className="flex items-center gap-2" data-id="element-204" data-mp="1zo94wzuv9xfw">
        <ExportMenu data-id="element-205" data-mp="1zlel9ajoggnj" />

        <Button onClick={openCreateForm} className="hidden h-9 rounded-xl brand-gradient px-4 text-white shadow-lg shadow-indigo-500/25 hover:opacity-95 sm:inline-flex" data-id="element-206" data-mp="24maqbrfrm8uj">
          <Plus className="mr-1.5 h-4 w-4" data-id="element-207" data-mp="2ec40co09efwo" />
          Add Student
        </Button>

        <ThemeToggle data-id="element-208" data-mp="2ee86zdpjlt0n" />

        <DropdownMenu data-id="element-209" data-mp="2jc054pgs6bda">
          <DropdownMenuTrigger asChild data-id="element-210" data-mp="2jci2xqj3cxnb">
            <button type="button" aria-label="Account menu" className="rounded-full ring-offset-2 ring-offset-background transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-211" data-mp="2j9nelnwyisom">
              <Avatar className="h-9 w-9 border border-[var(--glass-border)]" data-id="element-212" data-mp="7mjx4fg8e3ye">
                {user?.photoURL ? <AvatarImage src={user.photoURL} alt={displayName} data-id="element-213" data-mp="2pk1yzkx6lm2" /> : null}
                <AvatarFallback className="brand-gradient text-xs font-semibold text-white" data-id="element-214" data-mp="3tw4ktw0tmn80">
                  {initialsOf(displayName)}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64" data-id="element-215" data-mp="qgph1cj1d9vb">
            <DropdownMenuLabel data-id="element-216" data-mp="lmuqpq8xdwn9">
              <span className="block truncate text-sm font-semibold text-foreground" data-id="element-217" data-mp="lltcjcwm4u1b">{displayName}</span>
              <span className="block truncate text-xs font-normal text-muted-foreground" data-id="element-218" data-mp="gmp3qndloybi">{user?.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator data-id="element-219" data-mp="19cyr7oqthga1" />
            <DropdownMenuItem onClick={() => navigate('/settings')} data-id="element-220" data-mp="19bw3tqspdvyv">
              <UserRound className="mr-2 h-4 w-4" data-id="element-221" data-mp="tv7h4c7c59y3" />
              Account settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings')} data-id="element-222" data-mp="xxu3hxl8m2nw">
              <ShieldCheck className="mr-2 h-4 w-4" data-id="element-223" data-mp="jxt3ekcsqz9h" />
              Master password
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLock} data-id="element-224" data-mp="ovkw3gl9mpeq">
              <Lock className="mr-2 h-4 w-4" data-id="element-225" data-mp="oy7d06u1qh9m" />
              Lock app
            </DropdownMenuItem>
            <DropdownMenuSeparator data-id="element-226" data-mp="1cob3dci84kio" />
            <DropdownMenuItem variant="destructive" onClick={handleSignOut} data-id="element-227" data-mp="1hljqlri8beqs">
              <LogOut className="mr-2 h-4 w-4" data-id="element-228" data-mp="12w5ggh6twtac" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>;
}